import {
    getHipSpinePosture,
    putHipSpinePosture,
    type HipSpinePostureApiObject,
} from '@/api/hip-spine-study/hipSpinePosture';
import { getHipSpineLandmarks, putHipSpineLandmarks } from '@/api/hip-spine-study/hipSpineLandmark';
import {
    diffLandmarksForSave,
    mergeLandmarkUpdates,
    postureFieldsChanged,
} from '@/domain/anatomy/hipSpinePostureSave';
import type { PostureSavePlan } from './useHipSpinePostureSavePlan';
import { XRAY_HIP_SPINE_VIEW_IDS } from '@/api/xrays';
import { applyPostureData } from './hipSpineServices';
import { logger } from '@/util/logger';
import type { HipSpineStoreContext } from './hipSpineStore';

const log = logger('hip-spine');

/**
 * Resolve the server-authoritative posture after a save. `has_unapproved_changes` is
 * posture-level state owned by the posture endpoint, which recomputes it on every write and
 * CLEARS it when the posture matches its approved snapshot. A posture PUT returns the
 * recomputed flag in its body; a landmark-only save does not, so re-read the posture to pick
 * it up rather than assuming re-approval. Returns null when neither write ran.
 *
 * The re-read is BEST-EFFORT: the landmark write has already succeeded server-side, so a GET
 * failure (notably an abort when the dialog closes mid-flight) must not reject the save. A
 * reject would leave the edit overlay uncleared, re-PUTting the same landmarks on the next
 * mount (a spurious double save). On failure we return null and the flag re-syncs on the next
 * idle refresh.
 */
async function resolveSavedPosture(
    target: { caseId: number; studyId: number; posture: (typeof XRAY_HIP_SPINE_VIEW_IDS)[number] },
    postureResponse: HipSpinePostureApiObject | null,
    savedLandmarks: boolean,
    signal?: AbortSignal,
): Promise<HipSpinePostureApiObject | null> {
    if (postureResponse) return postureResponse;
    if (!savedLandmarks) return null;
    try {
        return await getHipSpinePosture(target.caseId, target.studyId, target.posture, { signal });
    } catch (error) {
        log.warning(
            'Posture re-read after a landmark save failed (flag re-syncs on idle): %s',
            error,
        );
        return null;
    }
}

/**
 * Persist a posture's save plan and apply the API responses as the source of truth.
 * Runs landmark + posture PUTs (only those the plan requires), replaces local state
 * from the responses, then rebuilds geometry. Does NOT clear edit overlays — the
 * sync store does that after this resolves. Throws on a PUT failure (caller handles);
 * the follow-up posture re-read is best-effort and never throws (see resolveSavedPosture).
 */
export async function persistPosture(
    ctx: HipSpineStoreContext,
    postureId: string,
    plan: PostureSavePlan,
    signal?: AbortSignal,
): Promise<void> {
    const { state, postureViewStates } = ctx;
    if (!state.caseId || !state.studyId) return;
    const posture = postureId as (typeof XRAY_HIP_SPINE_VIEW_IDS)[number];

    const landmarksResponse = plan.landmarkPayload.length
        ? await putHipSpineLandmarks(state.caseId, state.studyId, posture, {
              landmarks: plan.landmarkPayload,
              config: { signal },
          })
        : null;
    const postureResponse = plan.postureBody
        ? await putHipSpinePosture(state.caseId, state.studyId, posture, {
              body: plan.postureBody,
              config: { signal },
          })
        : null;

    const idx = state.hipSpinePostures.findIndex((p) => p.posture === postureId);
    if (idx < 0) return;

    const authoritativePosture = await resolveSavedPosture(
        { caseId: state.caseId, studyId: state.studyId, posture },
        postureResponse,
        landmarksResponse !== null,
        signal,
    );
    if (authoritativePosture) state.hipSpinePostures[idx] = authoritativePosture;

    const viewState = postureViewStates.get(postureId);
    if (viewState) {
        // The landmarks PUT echoes ONLY the changed landmarks (the backend returns
        // the diff, not the full set), so merge them into the existing full set by
        // name — replacing wholesale would drop every un-edited landmark. Unchanged
        // landmarks keep their last server values (also API-sourced).
        const landmarks = landmarksResponse
            ? mergeLandmarkUpdates(viewState.landmarks, landmarksResponse.landmarks)
            : viewState.landmarks;
        applyPostureData(viewState, state.hipSpinePostures[idx], landmarks);
    }
}

/**
 * Re-pull a posture's fields + landmarks from the server (idle sync). Skips the
 * geometry rebuild when nothing changed — important because rebuild resets angle
 * visibility. Callers must only invoke this for NON-dirty postures.
 */
export async function refreshPosture(
    ctx: HipSpineStoreContext,
    postureId: string,
    signal?: AbortSignal,
): Promise<void> {
    const { state, postureViewStates } = ctx;
    if (!state.caseId || !state.studyId) return;
    const posture = postureId as (typeof XRAY_HIP_SPINE_VIEW_IDS)[number];
    const idx = state.hipSpinePostures.findIndex((p) => p.posture === postureId);
    const viewState = postureViewStates.get(postureId);
    if (idx < 0 || !viewState) return;
    const current = state.hipSpinePostures[idx];

    const [postureObj, landmarksResp] = await Promise.all([
        getHipSpinePosture(state.caseId, state.studyId, posture, { signal }),
        getHipSpineLandmarks(state.caseId, state.studyId, posture, { signal }),
    ]);

    const fields = {
        side: postureObj.side,
        lumbarLevel: postureObj.lumbar_vertebra_end_plate,
        femur1: postureObj.femur_1_head_radius,
        femur2: postureObj.femur_2_head_radius,
    };
    const fieldsChanged = postureFieldsChanged(fields, current);
    const flagChanged = postureObj.has_unapproved_changes !== current.has_unapproved_changes;
    // Direction is intentional: compare the server response against the locally
    // displayed (allowed-name-filtered) set. Server landmarks outside that set are
    // correctly ignored. The displayed landmark set is fixed per posture at study
    // creation, so a name present server-side but absent locally cannot occur here
    // (would only matter under server-side mutation — out of scope).
    const landmarksChanged =
        diffLandmarksForSave(landmarksResp.landmarks, viewState.landmarks).length > 0;

    if (!fieldsChanged && !flagChanged && !landmarksChanged) return; // unchanged — preserve angle visibility
    state.hipSpinePostures[idx] = postureObj;
    if (fieldsChanged || landmarksChanged)
        applyPostureData(viewState, postureObj, landmarksResp.landmarks);
}
